/**
 * OkReserva - Contenido de Instrucciones Interactivas
 * Cada sección tiene: descripción, pasos, configuración y tips
 */

export const HELP_CONTENT = {

  // ==================== OPERACIONES ====================
  dashboard: {
    title: 'Dashboard Principal',
    emoji: '📊',
    color: 'from-blue-500 to-blue-700',
    description: 'El centro de mando de tu restaurante. Aquí ves todo lo que pasa hoy de un vistazo.',
    what: 'El Dashboard consolida en tiempo real las métricas más importantes del día: reservaciones activas, ocupación, no-shows esperados y tendencias clave. Es el primer lugar al que debes mirar al iniciar el turno.',
    steps: [
      { title: 'Revisa el resumen diario', desc: 'Al abrir, verás las reservaciones del día, cuántas están confirmadas, pendientes y canceladas.' },
      { title: 'Monitorea la ocupación', desc: 'El indicador de ocupación te muestra en qué porcentaje estás respecto a tu capacidad máxima.' },
      { title: 'Consulta el pronóstico de no-shows', desc: 'El sistema predice cuántos clientes probablemente no lleguen, para que puedas aceptar más reservaciones.' },
      { title: 'Revisa alertas pendientes', desc: 'Las alertas rojas/amarillas señalan acciones urgentes: recordatorios no enviados, depósitos vencidos, etc.' },
    ],
    config: [
      { title: 'Fecha del Dashboard', desc: 'Usa el selector de fecha en la barra superior para ver cualquier día pasado o futuro.' },
      { title: 'Widgets personalizables', desc: 'Puedes configurar qué métricas se muestran en Ajustes → Dashboard.' },
    ],
    tips: [
      { text: 'Revisa el Dashboard al inicio del turno de mediodía y de la noche para anticipar problemas operativos.', type: 'pro' },
      { text: 'Si el pronóstico de no-shows es mayor a 15%, considera llamar a los clientes sin depósito para confirmar.', type: 'warning' },
      { text: 'Los colores del indicador de ocupación: verde (< 70%), amarillo (70-90%), rojo (> 90%).', type: 'info' },
    ]
  },

  libro: {
    title: 'Libro Digital',
    emoji: '📖',
    color: 'from-indigo-500 to-indigo-700',
    description: 'Tu agenda de reservaciones en formato de libro, organizada por hora y sección del restaurante.',
    what: 'El Libro Digital reemplaza el libro físico de reservaciones. Muestra todas las reservaciones del día organizadas visualmente, con colores por estado, notas de cliente y el historial completo de cada visita.',
    steps: [
      { title: 'Selecciona la fecha', desc: 'Usa el selector de fecha en la barra superior para navegar entre días.' },
      { title: 'Identifica el estado de cada reservación', desc: 'Verde = confirmada, amarillo = pendiente, rojo = no-show, gris = cancelada.' },
      { title: 'Haz clic en cualquier reservación', desc: 'Al hacer clic, ves el historial del cliente, sus preferencias, número de visitas y gasto promedio.' },
      { title: 'Actualiza el estado al llegar el cliente', desc: 'Cuando llega, márcala como "Sentado". Si no llega, márcala como "No-show".' },
      { title: 'Agrega notas en tiempo real', desc: 'Puedes agregar notas a cualquier reservación durante el servicio (solicitudes especiales, quejas, etc.).' },
    ],
    config: [
      { title: 'Vista por sección', desc: 'Filtra el libro por sección del restaurante (terraza, salón principal, barra) con los filtros superiores.' },
      { title: 'Colores personalizados', desc: 'Configura los colores de cada turno en Ajustes → Libro Digital.' },
    ],
    tips: [
      { text: 'Usa las notas del historial del cliente para sorprenderlo: "Siempre pide mesa junto a la ventana" o "Cumpleaños en febrero".', type: 'pro' },
      { text: 'Imprime el libro del día al inicio de cada turno como respaldo en caso de fallo de internet.', type: 'warning' },
      { text: 'El ícono ⭐ junto al nombre indica que es un cliente frecuente (5+ visitas).', type: 'info' },
    ]
  },

  reservations: {
    title: 'Reservaciones',
    emoji: '📅',
    color: 'from-orange-500 to-orange-700',
    description: 'Gestión completa de reservaciones: crear, editar, confirmar y cancelar.',
    what: 'El módulo de Reservaciones es donde se concentra toda la operación de booking. Desde aquí puedes crear reservaciones manuales, ver el historial completo, gestionar confirmaciones y manejar depósitos.',
    steps: [
      { title: 'Crea una nueva reservación', desc: 'Haz clic en "+ Nueva reservación", llena el nombre, teléfono, fecha, hora y número de comensales.' },
      { title: 'Vincula a un cliente existente', desc: 'Al escribir el nombre o teléfono, el sistema busca automáticamente en tu base de clientes.' },
      { title: 'Asigna una mesa', desc: 'Selecciona la mesa preferida del cliente o deja que el sistema la asigne automáticamente.' },
      { title: 'Activa el recordatorio automático', desc: 'Marca la casilla para enviar recordatorio 24h antes por WhatsApp o email.' },
      { title: 'Gestiona el depósito', desc: 'Para grupos grandes o fechas especiales, puedes solicitar un depósito al confirmar.' },
    ],
    config: [
      { title: 'Política de cancelación', desc: 'Configura en Ajustes → Reservaciones cuántas horas antes se puede cancelar sin penalidad.' },
      { title: 'Horarios disponibles', desc: 'Define en Ajustes → Disponibilidad los turnos, intervalos de reservación y capacidad máxima por turno.' },
      { title: 'Confirmación automática', desc: 'Activa la confirmación automática para reservaciones online en Ajustes → Reservaciones.' },
    ],
    tips: [
      { text: 'Para grupos de 8 o más, siempre solicita un depósito. Reduce los no-shows en un 80%.', type: 'pro' },
      { text: 'Activa el campo de "Ocasión especial" — los clientes que lo llenan tienen 40% menos no-shows.', type: 'info' },
      { text: 'Revisa los teléfonos mal escritos antes de enviar recordatorios para evitar errores.', type: 'warning' },
    ]
  },

  waitlist: {
    title: 'Lista de Espera',
    emoji: '⏳',
    color: 'from-amber-500 to-amber-700',
    description: 'Gestiona la lista de espera cuando el restaurante está lleno.',
    what: 'La Lista de Espera te permite capturar a clientes que quieren entrar cuando no hay mesas disponibles. El sistema calcula el tiempo estimado de espera y notifica automáticamente cuando hay disponibilidad.',
    steps: [
      { title: 'Agrega un cliente a la lista', desc: 'Haz clic en "+ Agregar", ingresa nombre, teléfono y número de personas.' },
      { title: 'Comparte el QR de autoregistro', desc: 'Muestra el código QR en la entrada para que los clientes se registren solos con su teléfono.' },
      { title: 'Notifica cuando haya mesa disponible', desc: 'Haz clic en "Notificar" para enviar un WhatsApp/SMS al cliente diciendo que su mesa está lista.' },
      { title: 'Sienta al cliente', desc: 'Cuando llega, haz clic en "Sentar" y asigna la mesa. El cliente sale de la lista automáticamente.' },
    ],
    config: [
      { title: 'Tiempo de espera promedio', desc: 'Configura el tiempo promedio de vuelta de mesa para que el sistema calcule estimados precisos.' },
      { title: 'Mensaje de notificación', desc: 'Personaliza el mensaje de WhatsApp que recibe el cliente en Ajustes → Lista de Espera.' },
    ],
    tips: [
      { text: 'El QR de autoregistro reduce el trabajo del hostess hasta un 70% en noches de alta demanda.', type: 'pro' },
      { text: 'Notifica a los primeros 2-3 de la lista simultáneamente. El primero que confirme obtiene la mesa.', type: 'pro' },
      { text: 'Un cliente sin respuesta en 5 minutos puede ser movido al final de la lista o eliminado.', type: 'warning' },
    ]
  },

  'walk-in': {
    title: 'Fila Digital Walk-In',
    emoji: '📲',
    color: 'from-teal-500 to-teal-700',
    description: 'Cola digital en tiempo real para walk-ins con notificación por WhatsApp.',
    what: 'La Fila Digital es una evolución de la lista de espera tradicional. Los clientes pueden registrarse desde su teléfono con un QR, recibir actualizaciones en tiempo real y ser notificados cuando su mesa esté lista sin tener que quedarse parados en la entrada.',
    steps: [
      { title: 'Coloca el QR en la entrada del restaurante', desc: 'Los clientes escanean y se registran con su nombre, teléfono y número de personas.' },
      { title: 'Monitorea la fila en tiempo real', desc: 'Verás todos los grupos en espera con su tiempo de espera y posición en la fila.' },
      { title: 'Notifica al siguiente grupo', desc: 'Haz clic en "Notificar" — el cliente recibe un WhatsApp automático.' },
      { title: 'Confirma el seateo', desc: 'Al hacer "Sentar", el tiempo de espera real queda registrado para estadísticas.' },
    ],
    config: [
      { title: 'Tiempo de espera promedio', desc: 'Configura cuántos minutos sueles tardar en liberar una mesa para dar estimados precisos.' },
      { title: 'Activar notificación WhatsApp', desc: 'Requiere tener la integración Kapso activa. Ve a Configuración → Integraciones.' },
    ],
    tips: [
      { text: 'Un cliente con teléfono registrado puede moverse libremente (tomar un café, dar una vuelta) mientras espera. Aumenta la satisfacción enormemente.', type: 'pro' },
      { text: 'Marca como VIP a los clientes frecuentes para subirlos en la fila automáticamente.', type: 'pro' },
      { text: 'Si un cliente no responde en 5 minutos tras ser notificado, puedes moverlo al final.', type: 'warning' },
    ]
  },

  'floor-plan': {
    title: 'Plano de Sala',
    emoji: '🗺️',
    color: 'from-emerald-500 to-emerald-700',
    description: 'Vista visual de todas las mesas y su estado en tiempo real.',
    what: 'El Plano de Sala muestra la distribución visual de tu restaurante con el estado de cada mesa en tiempo real. Permite asignar mesas directamente desde el mapa y ver de un vistazo la ocupación por sección.',
    steps: [
      { title: 'Configura el plano inicial', desc: 'Ve a Ajustes → Plano de Sala para definir las secciones y colocar las mesas en su posición real.' },
      { title: 'Interpreta los colores', desc: 'Verde = disponible, naranja = reservada, rojo = ocupada, gris = sin servicio.' },
      { title: 'Asigna mesas a reservaciones', desc: 'Arrastra una reservación pendiente a una mesa disponible para asignarla.' },
      { title: 'Actualiza el estado manualmente', desc: 'Haz clic en cualquier mesa para cambiar su estado, asignar un mesero o agregar notas.' },
    ],
    config: [
      { title: 'Crear secciones', desc: 'En Ajustes → Plano, crea secciones como "Terraza", "Salón", "Privado", "Barra".' },
      { title: 'Capacidad por mesa', desc: 'Define la capacidad mínima y máxima de cada mesa para que el sistema las asigne correctamente.' },
    ],
    tips: [
      { text: 'Toma foto de la distribución real de tu restaurante y úsala como referencia para configurar el plano digitalmente.', type: 'info' },
      { text: 'Asigna secciones a meseros específicos para balancear la carga de trabajo automáticamente.', type: 'pro' },
    ]
  },

  // ==================== CLIENTES ====================
  customers: {
    title: 'Clientes (CRM)',
    emoji: '👥',
    color: 'from-purple-500 to-purple-700',
    description: 'Base de datos completa de tus clientes con historial de visitas y preferencias.',
    what: 'El CRM de OkReserva guarda automáticamente cada cliente que hace una reservación. Con el tiempo, construyes un perfil rico con historial de visitas, gasto promedio, preferencias, alérgenos y ocasiones especiales.',
    steps: [
      { title: 'Busca un cliente', desc: 'Escribe el nombre, teléfono o email en la barra de búsqueda para encontrarlo al instante.' },
      { title: 'Revisa su perfil completo', desc: 'Haz clic para ver su historial de visitas, gasto promedio, alérgenos registrados y notas internas.' },
      { title: 'Agrega notas internas', desc: 'Las notas internas son visibles solo para el personal y no para el cliente.' },
      { title: 'Segmenta clientes', desc: 'Usa los filtros para ver solo VIPs, clientes de cumpleaños próximo, inactivos, etc.' },
    ],
    config: [
      { title: 'Campos personalizados', desc: 'Agrega campos propios como "empresa", "referido por" o "vino de hotel" en Ajustes → CRM.' },
      { title: 'Integración automática', desc: 'Cada reservación online llena el CRM automáticamente sin esfuerzo manual.' },
    ],
    tips: [
      { text: 'Revisa el CRM antes de cada turno para identificar clientes VIP y avisar al equipo con anticipación.', type: 'pro' },
      { text: 'El campo "Fecha de cumpleaños" activa felicitaciones automáticas y te avisa el día de su reservación.', type: 'info' },
      { text: 'Exporta el CRM cada 3 meses como respaldo en Clientes → Exportar → CSV.', type: 'warning' },
    ]
  },

  loyalty: {
    title: 'Fidelización',
    emoji: '⭐',
    color: 'from-yellow-500 to-orange-500',
    description: 'Programa de puntos y recompensas para premiar a tus mejores clientes.',
    what: 'El módulo de Fidelización te permite crear un programa de lealtad de marca propia. Los clientes acumulan puntos por visitas y pueden canjearlos por beneficios exclusivos en tu restaurante.',
    steps: [
      { title: 'Configura el programa de puntos', desc: 'Define cuántos puntos vale cada visita o cada $100 consumido en Fidelización → Configuración.' },
      { title: 'Crea las recompensas', desc: 'Agrega recompensas canjeables como "Postre gratis con 500 puntos" o "10% de descuento con 1000 puntos".' },
      { title: 'Inscribe a los clientes', desc: 'Los clientes pueden inscribirse desde tu página de reservas o manualmente desde el CRM.' },
      { title: 'Registra el canje', desc: 'Cuando un cliente quiere canjear, busca su perfil y haz clic en "Canjear recompensa".' },
    ],
    config: [
      { title: 'Nombre del programa', desc: 'Dale un nombre de marca: "Club Sibarita", "Mesa de Honor", etc. en Fidelización → Configuración.' },
      { title: 'Reglas de acumulación', desc: 'Define si los puntos se acumulan por visita, por monto gastado o por ambos.' },
      { title: 'Vigencia de puntos', desc: 'Establece si los puntos expiran (recomendado: 12 meses de inactividad).' },
    ],
    tips: [
      { text: 'Los programas con nombre de marca propia tienen 3x más participación que los genéricos. Nómbralo bien.', type: 'pro' },
      { text: 'La primera recompensa debe ser alcanzable en 2-3 visitas para enganchar al cliente desde el principio.', type: 'pro' },
      { text: 'Comunica el programa activamente en mesas, menú y redes. Los programas de lealtad "secretos" no funcionan.', type: 'info' },
    ]
  },

  memberships: {
    title: 'Membresías',
    emoji: '👑',
    color: 'from-amber-500 to-yellow-600',
    description: 'Planes de suscripción mensual para generar ingresos recurrentes.',
    what: 'Las Membresías te permiten crear planes de suscripción mensuales o anuales para clientes frecuentes. Los suscriptores pagan una cuota fija y reciben beneficios exclusivos, generando ingresos predecibles para el restaurante.',
    steps: [
      { title: 'Crea un plan de membresía', desc: 'Ve a Membresías → Planes → + Nuevo Plan. Define nombre, precio, período y beneficios.' },
      { title: 'Define los beneficios claramente', desc: 'Los beneficios deben ser tangibles: "10% de descuento", "Copa de vino de bienvenida", "Reservación prioritaria".' },
      { title: 'Inscribe suscriptores', desc: 'Desde Membresías → Suscriptores puedes inscribir clientes manualmente o invitarlos por email.' },
      { title: 'Gestiona renovaciones', desc: 'El sistema te avisa 7 días antes del vencimiento para renovar o cancelar cada suscripción.' },
    ],
    config: [
      { title: 'Período de facturación', desc: 'Define si es mensual o anual. Los planes anuales con descuento tienen mejor conversión.' },
      { title: 'Plan destacado', desc: 'Marca el plan más popular como "Featured" para que se destaque visualmente.' },
      { title: 'Integración de pagos', desc: 'Conecta Stripe en Configuración → Pagos para cobrar membresías automáticamente.' },
    ],
    tips: [
      { text: 'El plan anual con 2 meses gratis convierte mejor que el equivalente a pagar mensual. Siempre ofrece esa opción.', type: 'pro' },
      { text: 'Limita cada plan a un máximo de 5 beneficios. Más opciones generan confusión y reducen la conversión.', type: 'pro' },
      { text: 'Nunca reveles cuántos suscriptores tienes al inicio. Cuando llegues a 20, empieza a mencionarlo.', type: 'info' },
    ]
  },

  allergens: {
    title: 'Alérgenos (Perfiles)',
    emoji: '⚠️',
    color: 'from-red-500 to-red-700',
    description: 'Registra los alérgenos y restricciones dietéticas de cada cliente.',
    what: 'El módulo de Alérgenos guarda las restricciones alimentarias de cada cliente en su perfil permanente. Cuando el cliente hace una reservación, el personal ve automáticamente sus alérgenos antes de que llegue.',
    steps: [
      { title: 'Abre el perfil de un cliente', desc: 'Ve a Clientes y busca al cliente que deseas actualizar.' },
      { title: 'Registra sus alérgenos', desc: 'Selecciona de la lista de los 14 alérgenos oficiales de la UE los que aplican para ese cliente.' },
      { title: 'Agrega restricciones dietéticas', desc: 'Además de alérgenos, puedes marcar si es vegano, vegetariano, sin gluten, kosher, etc.' },
      { title: 'Verifica en cada reservación', desc: 'El sistema muestra un ícono de alerta cuando hay alérgenos registrados al asignar la mesa.' },
    ],
    config: [
      { title: 'Alerta en reservaciones', desc: 'Activa en Ajustes → Alérgenos para que aparezca siempre el banner de alerta cuando hay restricciones.' },
      { title: 'Notificar a cocina', desc: 'Configura que al hacer check-in de un cliente con alérgenos se envíe alerta automática a cocina.' },
    ],
    tips: [
      { text: 'Pregunta siempre al confirmar reservaciones: "¿Tiene alguna alergia o restricción alimentaria?" e ingrésala en el momento.', type: 'pro' },
      { text: 'Los alérgenos son responsabilidad legal. Tomarse esto en serio puede evitar situaciones graves.', type: 'warning' },
      { text: 'Forma al equipo para que siempre consulte el sistema antes de servir a un cliente con alérgenos registrados.', type: 'warning' },
    ]
  },

  'allergen-menu': {
    title: 'Menú con Alérgenos',
    emoji: '🍽️',
    color: 'from-green-500 to-emerald-700',
    description: 'Construye tu menú digital con etiquetas de alérgenos por platillo.',
    what: 'El constructor de Menú con Alérgenos te permite digitalizar toda tu carta con los alérgenos de cada platillo. Los comensales pueden filtrar el menú antes de llegar para ver exactamente qué pueden comer de forma segura.',
    steps: [
      { title: 'Agrega tus platillos', desc: 'Ve a Menú Alérgenos → + Platillo. Ingresa nombre, categoría, precio y descripción.' },
      { title: 'Marca los alérgenos por platillo', desc: 'Selecciona cada alérgeno que contiene el platillo (de los 14 oficiales de la UE).' },
      { title: 'Agrega etiquetas dietéticas', desc: 'Marca si es vegano, sin gluten, keto, etc. para facilitar la búsqueda.' },
      { title: 'Usa el filtro de comensales', desc: 'En la pestaña "Filtro para Comensales", selecciona los alérgenos del cliente para ver qué puede comer.' },
    ],
    config: [
      { title: 'Disponibilidad del platillo', desc: 'Desactiva la casilla "Disponible" para ocultar temporalmente un platillo sin eliminarlo.' },
      { title: 'Categorías del menú', desc: 'Organiza el menú por categorías: Entradas, Sopas, Carnes, Postres, etc.' },
    ],
    tips: [
      { text: 'Comparte la URL del menú filtrado con el cliente antes de su reservación para una experiencia premium.', type: 'pro' },
      { text: 'Actualiza los alérgenos cada vez que cambie la receta de un platillo. Una variación en ingredientes puede ser crítica.', type: 'warning' },
      { text: 'Los restaurantes con menú de alérgenos publicado tienen 25% más de reservaciones de grupos con restricciones.', type: 'info' },
    ]
  },

  'acceso-garantizado': {
    title: 'Acceso Garantizado',
    emoji: '🛡️',
    color: 'from-gray-800 to-black',
    description: 'Club de membresías premium que garantizan mesa disponible siempre.',
    what: 'El módulo de Acceso Garantizado crea un club exclusivo de socios que nunca tienen que esperar ni preocuparse por disponibilidad. Es la versión premium del programa de membresías, diseñada para retener a tus clientes más valiosos.',
    steps: [
      { title: 'Elige el plan correcto para cada cliente', desc: 'Insider (frecuente casual), Elite (cliente importante), VIP Black (tu mejor cliente).' },
      { title: 'Registra al socio', desc: 'Ve a + Agregar socio, completa el perfil y selecciona el nivel de membresía.' },
      { title: 'Comunica los beneficios', desc: 'Envía al nuevo socio un email/WhatsApp con su tarjeta virtual de membresía y los beneficios.' },
      { title: 'Gestiona el acceso en operaciones', desc: 'Al llegar un socio, identifícalo en el sistema y aplica su beneficio de turno.' },
    ],
    config: [
      { title: 'Personaliza los beneficios por nivel', desc: 'Adapta los beneficios a lo que tu restaurante puede ofrecer de forma sostenible.' },
      { title: 'Define el proceso de reservación prioritaria', desc: 'Configura cuántas horas antes puede reservar cada nivel vs el público general.' },
    ],
    tips: [
      { text: 'Invita personalmente a los clientes candidatos — no lo hagas masivo. La exclusividad es el valor principal.', type: 'pro' },
      { text: 'Limita el plan VIP Black a máximo 20-30 socios para que la promesa de "mesa garantizada" sea sostenible.', type: 'warning' },
      { text: 'Los socios VIP gastan 3-4x más que clientes regulares. El costo del beneficio se recupera en la primera visita.', type: 'info' },
    ]
  },

  // ==================== INGRESOS ====================
  events: {
    title: 'Eventos',
    emoji: '🎉',
    color: 'from-pink-500 to-rose-600',
    description: 'Crea y vende tickets para eventos especiales en tu restaurante.',
    what: 'El módulo de Eventos te permite organizar noches especiales, cenas temáticas, maridajes o festividades y vender tickets directamente desde tu plataforma. Cada evento tiene su propia página de venta.',
    steps: [
      { title: 'Crea un nuevo evento', desc: 'Ve a Eventos → + Nuevo. Define título, fecha, hora, capacidad y precio de entrada.' },
      { title: 'Describe el evento', desc: 'Agrega una descripción atractiva, el menú especial del evento y quién es el chef o artista.' },
      { title: 'Publica la página de venta', desc: 'Activa el evento y el sistema genera automáticamente una URL para compartir.' },
      { title: 'Monitorea las ventas', desc: 'Ve cuántos tickets vendidos, ingresos generados y datos de los asistentes en tiempo real.' },
    ],
    config: [
      { title: 'Capacidad del evento', desc: 'Define el máximo de asistentes. Cuando se llena, el botón de compra se deshabilita automáticamente.' },
      { title: 'Política de reembolso', desc: 'Configura si los tickets son reembolsables y hasta cuántas horas antes.' },
    ],
    tips: [
      { text: 'Los eventos con lista de espera crean urgencia y aumentan las ventas. Actívala aunque no la necesites.', type: 'pro' },
      { text: 'Publica el evento con al menos 3 semanas de anticipación. Los últimos 5 días suelen ser los más activos en ventas.', type: 'info' },
      { text: 'Incluye fotos reales de eventos anteriores — las imágenes incrementan la conversión hasta un 60%.', type: 'pro' },
    ]
  },

  deposits: {
    title: 'Depósitos',
    emoji: '💳',
    color: 'from-emerald-600 to-teal-700',
    description: 'Solicita depósitos para confirmar reservaciones y eliminar no-shows.',
    what: 'El sistema de Depósitos permite solicitar un pago anticipado para confirmar reservaciones de alto valor. Es la herramienta más efectiva para reducir los no-shows, que en promedio representan pérdidas del 15-20% de ingresos.',
    steps: [
      { title: 'Activa la solicitud de depósito', desc: 'Al crear una reservación, activa "Solicitar depósito" y define el monto.' },
      { title: 'El cliente recibe un link de pago', desc: 'Automáticamente se envía al cliente un link para pagar el depósito en línea.' },
      { title: 'Monitorea el pago', desc: 'En Depósitos → Pendientes verás cuáles no han pagado aún.' },
      { title: 'Aplica el depósito al consumo', desc: 'El depósito se descuenta automáticamente de la cuenta final del cliente.' },
    ],
    config: [
      { title: 'Umbral automático de depósito', desc: 'Configura en Ajustes para que toda reservación mayor a X personas requiera depósito automáticamente.' },
      { title: 'Monto del depósito', desc: 'Recomendado: entre $200 y $500 MXN por persona para grupos de 6+.' },
      { title: 'Pasarela de pago', desc: 'Conecta Stripe o Mercado Pago en Configuración → Pagos para procesar depósitos.' },
    ],
    tips: [
      { text: 'Un depósito del 20-30% del ticket promedio reduce no-shows en más del 85%. No tiene que ser el total de la cuenta.', type: 'pro' },
      { text: 'Si el cliente cancela con 24h+ de anticipación, devuelve el depósito. Protege tu reputación.', type: 'warning' },
      { text: 'Menciona el depósito de forma amigable: "Para confirmar su reservación necesitamos una garantía de $300 por persona".', type: 'info' },
    ]
  },

  'private-dining': {
    title: 'Comedores Privados',
    emoji: '🍷',
    color: 'from-red-700 to-red-900',
    description: 'Gestiona salas privadas, menús especiales y reservaciones de eventos exclusivos.',
    what: 'El módulo de Comedores Privados te permite gestionar espacios exclusivos para eventos corporativos, celebraciones privadas y cenas especiales. Cada sala tiene su propio perfil con capacidad, precios y menús disponibles.',
    steps: [
      { title: 'Configura tus salas', desc: 'Ve a Comedores Privados → Salas → + Nueva Sala. Define nombre, capacidad, consumo mínimo y amenidades.' },
      { title: 'Recibe consultas', desc: 'Las reservaciones entran primero como "Consulta" para que puedas revisar disponibilidad.' },
      { title: 'Confirma o rechaza', desc: 'Al confirmar, el sistema bloquea esa sala en el calendario y notifica al cliente.' },
      { title: 'Asigna un menú especial', desc: 'Puedes crear menús cerrados específicos para cada evento en Comedores Privados → Menús.' },
    ],
    config: [
      { title: 'Consumo mínimo', desc: 'Define el consumo mínimo por sala. Es la mejor forma de proteger la rentabilidad del espacio.' },
      { title: 'Amenidades por sala', desc: 'Lista los equipos disponibles: proyector, sistema de audio, decoración incluida, etc.' },
    ],
    tips: [
      { text: 'Incluye al menos 3-4 fotos reales de cada sala. Las reservaciones privadas se basan 90% en lo visual.', type: 'pro' },
      { text: 'Crea paquetes temáticos: "Cena romántica", "Reunión de negocios", "Cumpleaños VIP" con precios todo incluido.', type: 'pro' },
      { text: 'El consumo mínimo protege al restaurante de perder ingresos por la exclusividad del espacio. No lo omitas.', type: 'warning' },
    ]
  },

  'gift-cards': {
    title: 'Tarjetas Regalo',
    emoji: '🎁',
    color: 'from-pink-400 to-pink-600',
    description: 'Vende y gestiona tarjetas de regalo digitales para tu restaurante.',
    what: 'Las Tarjetas Regalo digitales son ingresos anticipados y la mejor herramienta de marketing. Un cliente que regala una tarjeta trae un cliente nuevo a tu restaurante.',
    steps: [
      { title: 'Configura los montos disponibles', desc: 'Define las denominaciones que ofreces: $500, $1000, $2000 MXN.' },
      { title: 'Vende desde la plataforma', desc: 'El cliente compra en línea y recibe el código por email al instante.' },
      { title: 'Valida al momento del pago', desc: 'Ingresa el código en el punto de venta para verificar el saldo disponible.' },
      { title: 'Aplica el descuento', desc: 'El sistema deduce el monto de la tarjeta del total de la cuenta.' },
    ],
    config: [
      { title: 'Personalización visual', desc: 'Agrega el logo de tu restaurante a las tarjetas digitales en Ajustes → Tarjetas Regalo.' },
      { title: 'Vigencia', desc: 'Define en meses cuánto tiempo son válidas las tarjetas (recomendado: 12 meses).' },
    ],
    tips: [
      { text: 'Activa las tarjetas regalo en diciembre/enero, San Valentín y Día de las Madres. El 40% de las ventas anuales ocurren en esas fechas.', type: 'pro' },
      { text: 'Ofrece un 10% de bono en tarjetas de $2000+. "Pagas $2000 y tienes $2200 disponibles" es irresistible.', type: 'pro' },
    ]
  },

  cfdi: {
    title: 'Facturación SAT / CFDI',
    emoji: '🧾',
    color: 'from-slate-600 to-slate-800',
    description: 'Emite facturas electrónicas CFDI 4.0 directamente desde la plataforma.',
    what: 'El módulo de Facturación te permite emitir Comprobantes Fiscales Digitales (CFDI 4.0) cumpliendo con los requisitos del SAT mexicano. Los clientes pueden solicitar factura durante o después de su visita.',
    steps: [
      { title: 'Configura tus datos fiscales', desc: 'Ve a Facturación → Configuración fiscal. Ingresa tu RFC, razón social, CP y régimen fiscal.' },
      { title: 'Conecta tu PAC', desc: 'Ingresa el API key de tu Proveedor Autorizado de Certificación (FacturAPI, Finkok, etc.).' },
      { title: 'Activa la facturación', desc: 'Activa la casilla "Facturación habilitada" para comenzar a emitir CFDIs.' },
      { title: 'Crea una factura', desc: 'Ve a + Nueva Factura, ingresa el RFC del receptor, monto y descripción.' },
      { title: 'Timbra la factura', desc: 'Haz clic en "Timbrar" para que el PAC certifique la factura ante el SAT.' },
    ],
    config: [
      { title: 'Proveedor PAC', desc: 'Necesitas contratar un PAC como FacturAPI (facturapi.io) antes de poder timbrar. El costo es ~$1 MXN por factura.' },
      { title: 'Régimen fiscal', desc: 'Selecciona el régimen correcto. Si tienes dudas, consulta a tu contador.' },
      { title: 'Vigencia del certificado CSD', desc: 'El CSD del SAT tiene vigencia. Renuévalo en el portal del SAT antes de que venza.' },
    ],
    tips: [
      { text: 'Coloca un código QR en la mesa que lleve al cliente directamente al formulario de solicitud de factura. Reduce el trabajo manual enormemente.', type: 'pro' },
      { text: 'Las facturas globales (al público en general con RFC XAXX010101000) son válidas para ventas donde no solicitan factura individual.', type: 'info' },
      { text: 'Nunca canceles una factura timbrada sin consultar a tu contador. Puede tener implicaciones fiscales.', type: 'warning' },
    ]
  },

  // ==================== CANALES ====================
  whatsapp: {
    title: 'WhatsApp Automático',
    emoji: '💬',
    color: 'from-green-500 to-green-700',
    description: 'Automatiza los mensajes de WhatsApp para confirmaciones, recordatorios y seguimiento.',
    what: 'El módulo de WhatsApp conecta OkReserva con la API de WhatsApp Business para enviar mensajes automáticos en momentos clave: al confirmar reservación, 24h antes, después de la visita y para campañas de marketing.',
    steps: [
      { title: 'Conecta WhatsApp', desc: 'Ve a Configuración → Integraciones → Kapso para vincular tu número de WhatsApp Business.' },
      { title: 'Activa los mensajes automáticos', desc: 'En WhatsApp → Configuración, activa cada tipo de mensaje que quieres enviar.' },
      { title: 'Personaliza las plantillas', desc: 'Edita los textos de cada mensaje con el nombre del restaurante, tono y emojis.' },
      { title: 'Monitorea las entregas', desc: 'En WhatsApp → Historial, verás todos los mensajes enviados y su estado de entrega.' },
    ],
    config: [
      { title: 'Número de WhatsApp Business', desc: 'Necesitas un número dedicado para el restaurante, no un número personal.' },
      { title: 'Plantillas de WhatsApp', desc: 'Las plantillas deben aprobarse por Meta antes de usarse. El proceso tarda 24-48h.' },
      { title: 'Variables dinámicas', desc: 'Usa {nombre}, {fecha}, {hora}, {personas} en las plantillas para personalizarlas.' },
    ],
    tips: [
      { text: 'El recordatorio 24h antes con opción de "Confirmar" o "Cancelar" reduce no-shows hasta un 70%.', type: 'pro' },
      { text: 'No envíes más de 2 mensajes por reservación sin que el cliente lo solicite. El spam destruye la relación.', type: 'warning' },
      { text: 'Los mensajes enviados entre 9am y 8pm tienen 40% más de respuesta que fuera de ese horario.', type: 'info' },
    ]
  },

  followup: {
    title: 'Seguimiento Post-Visita',
    emoji: '📨',
    color: 'from-violet-500 to-violet-700',
    description: 'Automatiza los mensajes después de cada visita para capturar feedback y generar reseñas.',
    what: 'El módulo de Post-Visita envía mensajes automáticos unas horas después de que el cliente termina su visita. El objetivo es capturar su satisfacción, generar reseñas en Google y construir una relación continua.',
    steps: [
      { title: 'Configura el tiempo de envío', desc: 'Define cuántas horas después de la visita enviar el mensaje (recomendado: 3-6 horas).' },
      { title: 'Activa la encuesta de satisfacción', desc: 'El mensaje incluye un enlace a tu encuesta NPS para medir la experiencia.' },
      { title: 'Activa la solicitud de reseña', desc: 'Si el NPS es 8+, el sistema envía automáticamente el link de Google para dejar reseña.' },
      { title: 'Configura el mensaje de reactivación', desc: 'Define un mensaje especial para clientes que no han vuelto en 30/60/90 días.' },
    ],
    config: [
      { title: 'Canal de envío', desc: 'Puedes enviar por WhatsApp (requiere Kapso) o email (requiere SendPulse).' },
      { title: 'Filtro de NPS', desc: 'Solo clientes con NPS 9-10 reciben el link de Google. Protege tu reputación de comentarios negativos.' },
    ],
    tips: [
      { text: 'Los restaurantes que envían seguimiento post-visita tienen 3x más reseñas en Google en 6 meses.', type: 'pro' },
      { text: 'Si un cliente da NPS 1-6, el sistema puede alertar al gerente para contactarlo directamente y resolver el problema antes de que publique.', type: 'pro' },
      { text: 'El mensaje de reactivación a 30 días tiene tasas de respuesta del 15-20% si incluye un beneficio exclusivo.', type: 'info' },
    ]
  },

  'booking-page': {
    title: 'Página de Reservas',
    emoji: '🌐',
    color: 'from-cyan-500 to-blue-600',
    description: 'Tu página de reservaciones pública para integrar en tu sitio web.',
    what: 'OkReserva genera automáticamente una página de reservaciones de marca propia que puedes enlazar desde tu sitio web, Instagram y Google My Business. Los clientes reservan sin salir de tu ecosistema.',
    steps: [
      { title: 'Personaliza tu página', desc: 'Ve a Página de Reservas → Diseño. Agrega tu logo, fotos, colores de marca y descripción.' },
      { title: 'Copia el link y compártelo', desc: 'Tu URL es okreseva.com/r/tu-restaurante. Compártela en redes sociales, Linktree y tu bio de Instagram.' },
      { title: 'Integra en tu sitio web', desc: 'Copia el código de botón o iframe en Widgets para añadir el formulario directamente a tu web.' },
      { title: 'Activa Google Reserve', desc: 'En Canales → Google, conecta tu cuenta de Google My Business para que el botón "Reservar" aparezca en Maps.' },
    ],
    config: [
      { title: 'Horarios publicados', desc: 'Define cuáles horarios aparecen como disponibles y cuáles están ocultos para el público.' },
      { title: 'Tiempo mínimo de anticipación', desc: 'Define con cuántas horas de anticipación mínima se puede reservar online (recomendado: 2-4 horas).' },
      { title: 'Política de cancelación visible', desc: 'Los clientes deben ver claramente la política antes de confirmar para evitar disputas.' },
    ],
    tips: [
      { text: 'Agrega el botón de reservas en el primer o segundo link de tu Linktree/bio de Instagram. El 60% del tráfico viene de ahí.', type: 'pro' },
      { text: 'Activa la foto de portada con el mejor plato de tu restaurante. Las páginas con foto de portada tienen 2x más conversión.', type: 'info' },
    ]
  },

  // ==================== ANALÍTICAS ====================
  analytics: {
    title: 'Analytics',
    emoji: '📈',
    color: 'from-blue-600 to-blue-800',
    description: 'Métricas avanzadas de tu restaurante: ocupación, ingresos, tendencias y comportamiento.',
    what: 'El módulo de Analytics consolida todos los datos de tu restaurante en gráficas accionables. Ve tendencias de ocupación, análisis de no-shows, comportamiento de clientes y proyecciones de ingresos.',
    steps: [
      { title: 'Selecciona el período de análisis', desc: 'Usa el selector de fechas para ver semana, mes, trimestre o año.' },
      { title: 'Interpreta la tasa de ocupación', desc: 'La línea verde es tu ocupación real. El área naranja es tu capacidad máxima.' },
      { title: 'Identifica los días y horas pico', desc: 'El mapa de calor muestra cuándo estás más lleno para optimizar personal y reservaciones.' },
      { title: 'Analiza el funnel de reservaciones', desc: 'Ve cuántas reservaciones recibiste, confirmaste, completaste y cuántas terminaron en no-show.' },
    ],
    config: [
      { title: 'Configurar métricas visibles', desc: 'Personaliza qué widgets aparecen en el dashboard de analytics en Ajustes → Analytics.' },
      { title: 'Exportar datos', desc: 'Descarga cualquier gráfica como CSV en el ícono de descarga de cada widget.' },
    ],
    tips: [
      { text: 'El RevPASH (Revenue Per Available Seat Hour) es la métrica clave para restaurantes. Si no la conoces, pregúntale al soporte.', type: 'pro' },
      { text: 'Revisa Analytics cada lunes para la semana pasada. El patrón semanal es más útil que los datos diarios para tomar decisiones.', type: 'info' },
      { text: 'Si tu tasa de no-shows supera el 10%, activa depósitos obligatorios. Si es menor al 5%, puedes relajar la política.', type: 'pro' },
    ]
  },

  nps: {
    title: 'Feedback & NPS',
    emoji: '💯',
    color: 'from-indigo-500 to-purple-600',
    description: 'Mide la satisfacción de tus clientes y captura su feedback de forma automática.',
    what: 'El Net Promoter Score (NPS) es la medida estándar de satisfacción del cliente en la industria. Se mide con una pregunta simple: "¿Qué tan probable es que recomiendes este restaurante?" en escala del 1 al 10.',
    steps: [
      { title: 'Activa la encuesta automática', desc: 'Las encuestas se envían automáticamente tras cada visita (configurable en Post-Visita).' },
      { title: 'Lee los comentarios abiertos', desc: 'Los comentarios cualitativos son más valiosos que el número. Léelos cada semana.' },
      { title: 'Identifica a los detractores', desc: 'Clientes con NPS 1-6 requieren atención inmediata. El sistema los marca automáticamente.' },
      { title: 'Contacta a los promotores', desc: 'Clientes con NPS 9-10 son candidatos perfectos para tu programa de referidos.' },
    ],
    config: [
      { title: 'Umbral de alerta', desc: 'Configura para recibir notificación inmediata cuando llega un NPS menor a 6.' },
      { title: 'Preguntas adicionales', desc: 'Agrega hasta 2 preguntas adicionales a la encuesta base (espera, comida, servicio, etc.).' },
    ],
    tips: [
      { text: 'Un NPS mayor a 50 es excelente para restaurantes. Mayor a 70 es de clase mundial.', type: 'info' },
      { text: 'Lee cada comentario negativo antes del siguiente turno. El feedback inmediato es el más valioso.', type: 'pro' },
      { text: 'Los clientes que dan NPS 9-10 y dejan comentario son tus mejores embajadores. Escríbeles personalmente.', type: 'pro' },
    ]
  },

  benchmark: {
    title: 'Benchmark de Mercado',
    emoji: '🏆',
    color: 'from-orange-600 to-red-700',
    description: 'Compara tu desempeño con el promedio anónimo de restaurantes similares en OkReserva.',
    what: 'El Benchmark de Mercado muestra cómo se compara tu restaurante con el promedio anónimo de todos los restaurantes en OkReserva. Los datos son 100% anónimos — nadie puede identificar a ningún restaurante específico.',
    steps: [
      { title: 'Revisa tu posición', desc: 'Las tarjetas de comparación muestran si estás por arriba o debajo del mercado en cada métrica.' },
      { title: 'Interpreta el color del indicador', desc: 'Verde = mejor que el mercado, rojo = peor que el mercado, gris = en línea con el promedio.' },
      { title: 'Lee las recomendaciones personalizadas', desc: 'La sección de recomendaciones explica qué hacer para mejorar cada métrica.' },
      { title: 'Actualiza periódicamente', desc: 'Haz clic en "Actualizar" para obtener los benchmarks más recientes.' },
    ],
    config: [],
    tips: [
      { text: 'El benchmark se vuelve más preciso con el tiempo. Cuantas más reservaciones proceses, más representativos son tus datos.', type: 'info' },
      { text: 'Una tasa de no-shows menor al 5% te pone en el top 20% de los restaurantes de la red. Es un objetivo alcanzable.', type: 'pro' },
      { text: 'Los datos de benchmark son anónimos y agregados. Ningún restaurante puede ver tus datos individuales.', type: 'info' },
    ]
  },

  // ==================== EMPRESARIAL ====================
  corporate: {
    title: 'Portal Corporativo',
    emoji: '🏢',
    color: 'from-blue-700 to-indigo-800',
    description: 'Gestiona cuentas de hoteles, agencias y empresas que reservan frecuentemente.',
    what: 'El Portal Corporativo crea una relación B2B con empresas que envían clientes regularmente: hoteles, agencias de viaje, empresas con gastos de representación, etc. Cada cuenta tiene su propio historial, descuentos y contacto dedicado.',
    steps: [
      { title: 'Crea una cuenta corporativa', desc: 'Ve a Portal Corporativo → + Cuenta. Ingresa el nombre, tipo de empresa y datos de contacto.' },
      { title: 'Define el descuento corporativo', desc: 'Asigna un porcentaje de descuento exclusivo para esa cuenta (ej. 15% para clientes de un hotel partner).' },
      { title: 'Crea reservaciones en su nombre', desc: 'Desde la cuenta corporativa, crea reservaciones directamente con sus datos de contacto pre-llenados.' },
      { title: 'Monitorea el historial', desc: 'Cada cuenta muestra cuántas reservaciones ha hecho, cuántas personas y el gasto total acumulado.' },
    ],
    config: [
      { title: 'Tipos de empresa', desc: 'Clasifica las cuentas: empresa, hotel, agencia, gobierno. Esto facilita los reportes y la segmentación.' },
      { title: 'Notas internas por cuenta', desc: 'Usa las notas para guardar información que el equipo debe saber sobre ese cliente corporativo.' },
    ],
    tips: [
      { text: 'Los clientes corporativos gastan 2-3x más que los individuales y tienen tasas de no-show mucho menores.', type: 'pro' },
      { text: 'Visita a los hoteles del área con un kit de bienvenida: menú impreso, tarjeta de contacto y condiciones especiales.', type: 'pro' },
      { text: 'Ofrece facturación CFDI directamente desde el portal para empresas que necesiten comprobante fiscal.', type: 'info' },
    ]
  },

  personal: {
    title: 'Programación de Personal',
    emoji: '👨‍🍳',
    color: 'from-orange-500 to-amber-600',
    description: 'Organiza turnos, horarios y la asignación de personal conectado a tu calendario de reservaciones.',
    what: 'El módulo de Programación de Personal conecta tus reservaciones con los turnos del equipo. Sabes exactamente cuánto personal tienes programado para cada servicio y si es suficiente para la demanda esperada.',
    steps: [
      { title: 'Registra a tu equipo', desc: 'Ve a Personal → Equipo → + Empleado. Agrega nombre, rol, tarifa por hora y datos de contacto.' },
      { title: 'Crea los turnos semanales', desc: 'En la vista de horario, haz clic en cualquier celda del calendario para agregar un turno.' },
      { title: 'Verifica la cobertura', desc: 'Compara el número de turnos activos con las reservaciones del día para asegurar cobertura.' },
      { title: 'Navega entre semanas', desc: 'Usa las flechas de navegación para ver semanas pasadas o futuras.' },
    ],
    config: [
      { title: 'Roles del equipo', desc: 'Usa los colores por rol para identificar rápidamente en el calendario quién está cubriendo qué.' },
      { title: 'Tarifa por hora', desc: 'Ingresa la tarifa de cada empleado para calcular el costo laboral proyectado por turno.' },
    ],
    tips: [
      { text: 'Cruza la vista del calendario de personal con las reservaciones antes de aprobar turnos. Si hay un evento de 80 personas, necesitas staffing extra.', type: 'pro' },
      { text: 'Comparte el calendario de turnos por WhatsApp cada semana para que el equipo tenga claridad.', type: 'info' },
      { text: 'El costo laboral ideal en restaurantes es 25-35% de los ingresos. Úsalo como referencia al programar personal.', type: 'pro' },
    ]
  },

  // ==================== MARKETING ====================
  marketing: {
    title: 'Campañas de Marketing',
    emoji: '📣',
    color: 'from-rose-500 to-pink-700',
    description: 'Crea campañas de email y WhatsApp para clientes segmentados.',
    what: 'El módulo de Marketing te permite crear campañas dirigidas a segmentos específicos de tu base de clientes. Desde promociones de temporada hasta mensajes de reactivación para clientes inactivos.',
    steps: [
      { title: 'Selecciona el segmento objetivo', desc: 'Define a quién va dirigida la campaña: todos los clientes, los VIP, los inactivos de 60 días, los de cumpleaños este mes, etc.' },
      { title: 'Elige el canal', desc: 'Email (SendPulse) o WhatsApp (Kapso). El WhatsApp tiene mejor apertura pero requiere más cuidado.' },
      { title: 'Redacta el mensaje', desc: 'Sé conciso y ve directo al valor: qué ofreces, cuándo y cómo reservar.' },
      { title: 'Programa o envía inmediatamente', desc: 'Puedes enviar al instante o programar para el mejor horario (martes-jueves, 10am-12pm).' },
    ],
    config: [
      { title: 'Conectar SendPulse', desc: 'Configura tus credenciales de SendPulse en Admin → Integraciones para enviar campañas de email.' },
      { title: 'Conectar Kapso', desc: 'Configura Kapso en Admin → Integraciones para campañas de WhatsApp.' },
    ],
    tips: [
      { text: 'Las campañas de "te extrañamos" a clientes inactivos de 45-60 días tienen 20-25% de conversión cuando incluyen un beneficio.', type: 'pro' },
      { text: 'Envía máximo 1 campaña de marketing por mes. Más frecuencia genera bajas de suscripción.', type: 'warning' },
      { text: 'El martes o miércoles a las 11am es el mejor horario para enviar campañas de restaurante según datos de la industria.', type: 'info' },
    ]
  },

  'risk-dashboard': {
    title: 'Gestión de No-Shows',
    emoji: '🚫',
    color: 'from-red-500 to-red-700',
    description: 'Identifica y mitiga los riesgos de no-show antes de que impacten tus ingresos.',
    what: 'El módulo de No-Shows usa inteligencia artificial para predecir qué reservaciones tienen mayor probabilidad de no presentarse, basándose en el historial del cliente, el canal de reservación, el día y la hora.',
    steps: [
      { title: 'Revisa el índice de riesgo', desc: 'Cada reservación tiene un color de riesgo: verde (bajo), amarillo (medio), rojo (alto).' },
      { title: 'Toma acción preventiva', desc: 'Para las reservaciones de alto riesgo, llama directamente o solicita confirmación por WhatsApp.' },
      { title: 'Activa overbooking inteligente', desc: 'El sistema sugiere cuántas reservaciones adicionales puedes aceptar basándose en el pronóstico de no-shows.' },
      { title: 'Registra el resultado', desc: 'Al marcar un no-show, el sistema aprende y mejora la predicción futura para ese cliente.' },
    ],
    config: [
      { title: 'Umbral de alerta automática', desc: 'Configura para recibir notificación cuando una reservación alcance un riesgo "alto".' },
      { title: 'Lista negra', desc: 'Los clientes con 3+ no-shows pueden bloquearse automáticamente en Ajustes → No-Shows.' },
    ],
    tips: [
      { text: 'Una llamada personal de 2 minutos a una reservación de alto riesgo reduce el no-show en un 90%.', type: 'pro' },
      { text: 'No apliques overbooking más del 10-15% de tu capacidad. El riesgo de tener más clientes que mesas es peor que los no-shows.', type: 'warning' },
      { text: 'Los no-shows tienen un patrón: viernes y sábado son los días con más no-shows, no los laborales.', type: 'info' },
    ]
  },

  // ==================== CONFIGURACIÓN ====================
  integrations: {
    title: 'Integraciones',
    emoji: '🔌',
    color: 'from-gray-700 to-gray-900',
    description: 'Conecta OkReserva con Kapso (WhatsApp) y SendPulse (Email).',
    what: 'Las integraciones conectan OkReserva con los servicios externos que manejan la comunicación con tus clientes. Kapso gestiona todos los mensajes de WhatsApp y SendPulse todos los emails transaccionales y de marketing.',
    steps: [
      { title: 'Configura Kapso (WhatsApp)', desc: 'Agrega tu KAPSO_API_KEY en el panel de Secrets. Haz clic en "Verificar conexión" para confirmar.' },
      { title: 'Configura SendPulse (Email)', desc: 'Agrega tu SENDPULSE_API_USER_ID y SENDPULSE_API_SECRET en Secrets. Verifica la conexión.' },
      { title: 'Prueba el envío', desc: 'Envía un WhatsApp y un email de prueba desde los módulos correspondientes.' },
      { title: 'Activa los mensajes automáticos', desc: 'Una vez conectados, activa los mensajes automáticos en los módulos de WhatsApp y Post-Visita.' },
    ],
    config: [
      { title: 'KAPSO_API_KEY', desc: 'Consigue tu API key en kapso.ai → Configuración → API. Es secreta, nunca la compartas.' },
      { title: 'SENDPULSE credentials', desc: 'En sendpulse.com → API Settings obtén el API User ID y API Secret.' },
    ],
    tips: [
      { text: 'Prueba siempre las integraciones con mensajes de prueba antes de activar los automáticos en producción.', type: 'warning' },
      { text: 'Si Kapso no conecta, verifica que el API key esté en el panel de Secrets, no en el código.', type: 'info' },
      { text: 'Verifica la conexión al inicio de cada semana para detectar credenciales vencidas antes de que afecten las operaciones.', type: 'pro' },
    ]
  },

  'menus-cartas': {
    title: 'Menús & Cartas',
    emoji: '🍽️',
    color: 'from-orange-400 to-orange-600',
    description: 'Gestiona todos tus menús y platillos con fotos, videos de YouTube, alérgenos y categorías.',
    what: 'Menús & Cartas es tu carta digital completa. Crea menús (carta del día, menú ejecutivo, carta de vinos) y agrega platillos a cada uno con fotos subidas desde tu dispositivo, video de YouTube, precios, costos, etiquetas de alérgenos y categorías. Los menús activos se publican automáticamente en el Widget Carta Express.',
    steps: [
      { title: 'Crea tu primer menú', desc: 'Haz clic en "Nuevo" en el panel izquierdo. Ponle un nombre (p.ej. "Carta del día") y actívalo.' },
      { title: 'O usa una plantilla', desc: 'Clic en el ícono ✨ para elegir entre Menú Ejecutivo, Carta de Vinos o Carta Completa — se crean con platillos de ejemplo listos para editar.' },
      { title: 'Agrega platillos', desc: 'Selecciona el menú y haz clic en "+ Platillo". Llena nombre, descripción, precio y categoría.' },
      { title: 'Sube fotos', desc: 'En el modal de platillo, sube hasta varias fotos desde tu dispositivo. La foto con ⭐ es la principal.' },
      { title: 'Marca alérgenos', desc: 'Selecciona los alérgenos presentes (gluten, lácteos, mariscos…). Aparecen como emojis en la carta para los comensales.' },
      { title: 'Filtra por categoría', desc: 'En el panel derecho, usa las pestañas de categorías para ver platillos filtrados por tipo.' },
    ],
    config: [
      { title: 'Menú activo vs. inactivo', desc: 'Solo los menús marcados como "Activo" aparecen en el Widget Carta Express. Desactiva temporalmente sin borrar.' },
      { title: 'Vista cuadrícula / lista', desc: 'Cambia la vista de platillos con los botones de la esquina superior derecha del panel de platillos.' },
      { title: 'Platillo destacado', desc: 'El ícono ⭐ en la carta marca un platillo como "Destacado", que aparece primero en el widget.' },
    ],
    tips: [
      { text: 'Usa fotos reales de tus platillos — aumenta el valor percibido y los clientes eligen más fácilmente.', type: 'pro' },
      { text: 'Las plantillas son un punto de partida, no un catálogo definitivo. Edita precios y descripciones con los de tu restaurante.', type: 'warning' },
      { text: 'El video de YouTube permite mostrar la preparación del platillo o su presentación. Ideal para platillos especiales o de alto margen.', type: 'info' },
    ]
  },

  'widget-carta-express': {
    title: 'Widget Carta Express',
    emoji: '🛒',
    color: 'from-emerald-500 to-emerald-700',
    description: 'Permite a tus clientes reservar mesa y elegir platillos anticipadamente, todo en un solo flujo embebido en tu web.',
    what: 'El Widget Carta Express combina el widget de reservas con una carta digital interactiva. Los clientes eligen fecha y hora, luego navegan tu carta y agregan platillos al pedido anticipado — todo antes de confirmar su reserva. Tú recibes la reserva con el pre-pedido incluido, lo que acelera el servicio y aumenta el ticket promedio.',
    steps: [
      { title: 'Crea tus menús primero', desc: 'Ve a Menús & Cartas y crea al menos un menú con platillos activos. El widget los carga automáticamente.' },
      { title: 'Personaliza el widget', desc: 'En la pestaña "Configurar", ajusta color principal, título, subtítulo y texto del botón de confirmación.' },
      { title: 'Prueba la vista en vivo', desc: 'En "Vista en vivo", navega todo el flujo: fecha → carta → datos → confirmación. Así lo verán tus clientes.' },
      { title: 'Copia el código de integración', desc: 'Ve a "Integrar en web" y copia el script. Pégalo en tu sitio web, landing page o CMS.' },
      { title: 'Activa el pedido anticipado', desc: 'Al recibir reservas, el sistema incluye el listado de platillos seleccionados en los datos de la reserva.' },
    ],
    config: [
      { title: 'Mostrar precios', desc: 'Activa/desactiva la visualización de precios en la carta del widget.' },
      { title: 'Filtro de categorías', desc: 'Muestra u oculta el filtro de categorías en el paso de selección de platillos.' },
      { title: 'Destacados primero', desc: 'Los platillos marcados como ⭐ Destacados aparecerán al inicio de la carta.' },
      { title: 'Alérgenos visibles', desc: 'Muestra los íconos de alérgenos en la carta del widget para informar a comensales con restricciones.' },
    ],
    tips: [
      { text: 'Ofrece un descuento del 5-10% a quien reserve con pedido anticipado — incrementa las conversiones y fidelización.', type: 'pro' },
      { text: 'Asegúrate de tener al menos 4-5 platillos activos antes de activar el widget — una carta vacía disuade.', type: 'warning' },
      { text: 'El pre-pedido llega al restaurante con la reserva. El equipo puede preparar anticipadamente, reduciendo tiempos de espera.', type: 'info' },
    ]
  },

  settings: {
    title: 'Ajustes',
    emoji: '⚙️',
    color: 'from-gray-500 to-gray-700',
    description: 'Configuración general de tu restaurante: perfil, horarios, capacidad y preferencias.',
    what: 'Los Ajustes son la base de toda la operación de OkReserva. Aquí configuras el perfil de tu restaurante, los horarios de servicio, la capacidad máxima, la política de cancelación y mucho más.',
    steps: [
      { title: 'Completa el perfil del restaurante', desc: 'Nombre, dirección, teléfono, logo y fotos. Estos datos aparecen en tu página de reservas pública.' },
      { title: 'Configura los horarios de servicio', desc: 'Define qué días abres, en qué horarios y los turnos disponibles (comida/cena).' },
      { title: 'Establece la capacidad total', desc: 'El número total de comensales que puedes atender simultáneamente es crítico para el sistema de reservaciones.' },
      { title: 'Define la política de cancelación', desc: 'Con cuántas horas de anticipación se puede cancelar y si hay penalidad.' },
    ],
    config: [],
    tips: [
      { text: 'Completa el 100% del perfil desde el primer día. Los campos vacíos afectan la página pública de reservas.', type: 'warning' },
      { text: 'Revisa la capacidad máxima mensualmente si tu restaurante tiene terrazas o espacios variables.', type: 'info' },
    ]
  },
};
